import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeartbeat, faTimes, faSearch, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Link, useLocation } from 'react-router-dom';
import './App.css';
import './Services.css';

function BookingTracker() {
  const [isMenuActive, setIsMenuActive] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showPhonePrompt, setShowPhonePrompt] = useState(false);
  const [patientData, setPatientData] = useState(null);
  const [services, setServices] = useState({});
  const [statusCodes, setStatusCodes] = useState({});

  const location = useLocation();
  
  useEffect(() => {
    // Check if phone number exists in navigation state
    if (location.state && location.state.phoneNumber) {
      setPhoneNumber(location.state.phoneNumber);
      fetchPatientData(location.state.phoneNumber);
    } else {
      setShowPhonePrompt(true);
    }
    
    // Fetch status codes
    fetchStatusCodes();
  }, [location]);
  
  const fetchStatusCodes = async () => {
    try {
      const response = await fetch('https://hospitalbackend-446302.el.r.appspot.com/code/Booking_Status_Cd');
      if (!response.ok) {
        throw new Error('Failed to fetch status codes');
      }
      const data = await response.json();
      
      // Create a mapping of code values to descriptions
      const codeMap = {};
      data.forEach(item => {
        codeMap[item.Code_Value] = item.Code_Desc;
      });
      
      console.log("Status Codes:", codeMap);
      setStatusCodes(codeMap);
    } catch (err) {
      console.error('Error fetching status codes:', err);
      setError('Failed to load status information. Please try again later.');
    }
  };

  const fetchPatientData = async (phone) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Fetch patient data by phone number
      const response = await fetch('https://hospitalbackend-446302.el.r.appspot.com/patients');
      if (!response.ok) {
        throw new Error('Failed to fetch patient data');
      }
      
      const patients = await response.json();
      const patient = patients.find(p => p.Phone_Number === phone);
      
      if (!patient) {
        setError('No patient found with this phone number');
        setIsLoading(false);
        return;
      }
      
      setPatientData(patient);
      
      // Fetch bookings for this patient
      await fetchBookings(patient.Patient_Id);
      
    } catch (err) {
      console.error('Error:', err);
      setError('An error occurred while fetching data. Please try again.');
      setIsLoading(false);
    }
  };
  
  const fetchBookings = async (patientId) => {
    try {
      // Fetch all bookings
      const bookingsResponse = await fetch('https://hospitalbackend-446302.el.r.appspot.com/bookings');
      if (!bookingsResponse.ok) {
        throw new Error('Failed to fetch bookings');
      }
      
      const allBookings = await bookingsResponse.json();
      const patientBookings = allBookings.filter(booking => booking.Patient_Id === patientId);
      
      if (patientBookings.length === 0) {
        setBookings([]);
        setIsLoading(false);
        return;
      }
      
      // Fetch booking services
      const bookingServicesResponse = await fetch('https://hospitalbackend-446302.el.r.appspot.com/booking-services');
      if (!bookingServicesResponse.ok) {
        throw new Error('Failed to fetch booking services');
      }
      
      const bookingServices = await bookingServicesResponse.json();
      
      // Fetch all services for reference
      await fetchAllServices();
      
      // Map bookings with their services
      const bookingsWithServices = patientBookings.map(booking => {
        const relatedServices = bookingServices.filter(
          service => service.Booking_Id === booking.Booking_Id
        );
        
        return {
          ...booking,
          services: relatedServices
        };
      });
      
      // Sort bookings by date
      bookingsWithServices.sort((a, b) => 
        new Date(b.Booking_Date_Time) - new Date(a.Booking_Date_Time)
      );
      
      console.log("Bookings with services:", bookingsWithServices);
      setBookings(bookingsWithServices);
      setIsLoading(false);
      
    } catch (err) {
      console.error('Error:', err);
      setError('An error occurred while fetching bookings. Please try again.');
      setIsLoading(false);
    }
  };
  
  const fetchAllServices = async () => {
    try {
      const servicesResponse = await fetch('https://hospitalbackend-446302.el.r.appspot.com/services');
      if (!servicesResponse.ok) {
        throw new Error('Failed to fetch services');
      }
      
      const allServices = await servicesResponse.json();
      const servicesMap = {};
      
      allServices.forEach(service => {
        servicesMap[service.Service_Id] = service.Service_Name;
      });
      
      setServices(servicesMap);
      
    } catch (err) {
      console.error('Error fetching services:', err);
    }
  };

  const handlePhoneSubmit = (e) => {
    e.preventDefault();
    if (phoneNumber && phoneNumber.trim() !== '') {
      setShowPhonePrompt(false);
      fetchPatientData(phoneNumber);
    }
  };

  const toggleMenu = () => {
    setIsMenuActive(!isMenuActive);
  };
  
  // Get status name based on code
  const getStatusName = (code) => {
    // For debugging
    console.log(`Looking up status code: ${code}, Available codes:`, statusCodes);
    return statusCodes[code] || `Status Code: ${code}`;
  };
  
  // Get step number for progress tracker
  const getStepNumber = (statusCode) => {
    const statusMap = {
      '111': 1, // Booking Service Pending
      '112': 2, // Booking Service Accepted
      '114': 2, // Booking Service Cancelled
      '115': 2, // Booking Service Declined
      '113': 3, // Booking Service In Progress
      '116': 4  // Booking Service Completed
    };
    
    return statusMap[statusCode] || 1;
  };
  
  // Format the patient name
  const formatPatientName = (patient) => {
    if (!patient) return '';
    
    return [patient.F_Name, patient.M_Name, patient.L_Name]
      .filter(Boolean)
      .join(' ');
  };
  
  // Format the date
  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  return (
    <div className="booking-tracker-container">
      <header className="header">
        <Link to="/" className="logo">
          <FontAwesomeIcon icon={faHeartbeat} /> Hospital
        </Link>
        <nav className={`navbar ${isMenuActive ? 'active' : ''}`}>
          <Link to="/">home</Link>
          <a href="#services">services</a>
          <a href="#about">about</a>
          <a href="#doctors">doctors</a>
          <a href="#book">book</a>
          <a href="#review">review</a>
          <a href="#Payments">Payments</a>
        </nav>

        <button
          id="menu-btn"
          className={isMenuActive ? 'fa-times' : ''}
          onClick={toggleMenu}
        >
          <FontAwesomeIcon icon={isMenuActive ? faTimes : faHeartbeat} />
        </button>
      </header>
      
      <div className="main-content">
        <h2 className="section-title">Your Bookings</h2>
        
        {showPhonePrompt && (
          <div className="phone-prompt-overlay">
            <div className="phone-prompt">
              <h3>Enter Your Phone Number</h3>
              <p>Please enter your phone number to view your bookings</p>
              <form onSubmit={handlePhoneSubmit}>
                <input
                  type="text"
                  placeholder="Phone Number"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  required
                />
                <button type="submit" className="btn">
                  <FontAwesomeIcon icon={faSearch} /> Find Bookings
                </button>
              </form>
            </div>
          </div>
        )}
        
        {isLoading && (
          <div className="loading-indicator">
            <FontAwesomeIcon icon={faSpinner} spin />
            <p>Loading your bookings...</p>
          </div>
        )}
        
        {error && (
          <div className="error-message">
            <p>{error}</p>
            <button 
              className="btn" 
              onClick={() => setShowPhonePrompt(true)}>
              Try Another Number
            </button>
          </div>
        )}
        
        {!isLoading && !error && bookings.length === 0 && patientData && (
          <div className="no-bookings">
            <p>No bookings found for {formatPatientName(patientData)}.</p>
            <Link to="/book" className="btn">Book Now</Link>
          </div>
        )}
        
        {!isLoading && !error && bookings.length > 0 && (
          <div className="bookings-grid">
            {bookings.map((booking) => (
              <div className="booking-card" key={booking.Booking_Id}>
                <h3>Booking #{booking.Booking_Id}</h3>
                <p className="booking-date">
                  {formatDate(booking.Booking_Date_Time)}
                </p>
                
                <div className="progress-tracker">
                  <div className="progress-line"></div>
                  <div className="progress-steps">
                    <div className={`step ${getStepNumber(booking.Booking_Status_Cd) >= 1 ? 'active' : ''}`}>
                      <div className="step-marker">1</div>
                      <div className="step-label">Pending</div>
                    </div>
                    <div className={`step ${getStepNumber(booking.Booking_Status_Cd) >= 2 ? 'active' : ''}`}>
                      <div className="step-marker">2</div>
                      <div className="step-label">
                        {booking.Booking_Status_Cd === '114' ? 'Cancelled' : 
                         booking.Booking_Status_Cd === '115' ? 'Declined' : 'Accepted'}
                      </div>
                    </div>
                    <div className={`step ${getStepNumber(booking.Booking_Status_Cd) >= 3 ? 'active' : ''}`}>
                      <div className="step-marker">3</div>
                      <div className="step-label">In Progress</div>
                    </div>
                    <div className={`step ${getStepNumber(booking.Booking_Status_Cd) >= 4 ? 'active' : ''}`}>
                      <div className="step-marker">4</div>
                      <div className="step-label">Completed</div>
                    </div>
                  </div>
                </div>
                
                <div className="booking-details">
                  <div className="detail-item">
                    <strong>Patient:</strong> {formatPatientName(patientData)}
                  </div>
                  <div className="detail-item">
                    <strong>Phone:</strong> {patientData?.Phone_Number}
                  </div>
                  <div className="detail-item">
                    <strong>Status:</strong> {getStatusName(booking.Booking_Status_Cd)}
                  </div>
                  <div className="detail-item services-list">
                    <strong>Services:</strong>
                    <ul>
                      {booking.services.map((service) => (
                        <li key={service.Booking_Service_Id}>
                          {services[service.Service_Id] || `Service #${service.Service_Id}`}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default BookingTracker;